import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormButtonsConfig } from "@mipro/components";
import { ConsultationMergeMoveService } from "@project/consultation-merge-move/consultation-merge-move.service";
import { IEpisode } from "@project/models/episodes/episode";
import { BehaviorSubject, catchError, combineLatestWith, filter, from, Observable, of, switchMap, tap } from "rxjs";
import { EpisodeService } from "./episode.service";

@Component({
    selector: "merge-episodes-dialog",
    templateUrl: "./merge-episode-dialog.component.html",
    styleUrls: ["./merge-episode-dialog.component.scss"]
})
export class MergeEpisodesDialogComponent implements OnInit {
    private _selectedEpisodeIds: string[] = [];
    private _visible = false;
    private _unmergeEpisodeIdChange = new BehaviorSubject<string>(null);
    private _visibleChange = new BehaviorSubject<boolean>(this._visible);

    title: string;
    loadingEpisodes = false;
    episodes$: Observable<IEpisode[]>;
    buttonsConfig = <FormButtonsConfig>{
        cancelFunc: () => (this.visible = false),
        saveFunc: () => this.confirm(),
        cancelLabel: "Cancel"
    };
    confirmInProgress = false;

    @Input() set visible(visible: boolean) {
        if (visible === this._visible) {
            return;
        }

        this._visible = visible;
        this.visibleChange.next(visible);
        this._visibleChange.next(visible);
    }
    get visible() {
        return this._visible;
    }
    @Input() operation: "Merge" | "Unmerge" = "Merge";
    @Input() set unmergeEpisodeId(episodeId: string) {
        this._unmergeEpisodeIdChange.next(episodeId);
    }

    @Output() readonly visibleChange = new EventEmitter<boolean>();
    @Output() readonly confirmed = new EventEmitter<void>();

    constructor(private mergeMoveService: ConsultationMergeMoveService, private episodeService: EpisodeService) {}

    ngOnInit() {
        if (this.operation === "Merge") {
            this.title = "Select the Master Episode from the Existing Episodes to Merge";
            this.buttonsConfig.saveLabel = "Merge with Selected Master Episode";
            this.episodes$ = this.loadMergeEpisodes$;
        } else if (this.operation === "Unmerge") {
            this.title = "Select the Episodes to Unmerge";
            this.buttonsConfig.saveLabel = "Unmerge Episodes";
            this.episodes$ = this.loadUnmergeEpisodes$;
        }
    }

    get selectedEpisodeId(): string | string[] {
        if (this.operation === "Merge") {
            return this._selectedEpisodeIds[0];
        }
        return this._selectedEpisodeIds;
    }
    set selectedEpisodeId(episodeId: string | string[]) {
        if (typeof episodeId === "string") {
            episodeId = [episodeId];
        }
        this._selectedEpisodeIds = episodeId ?? [];
    }

    get isAcceptDisabled() {
        return this._selectedEpisodeIds.length === 0;
    }

    async confirm() {
        try {
            this.confirmInProgress = true;
            if (this.operation === "Merge") {
                await this.mergeMoveService.mergeEpisode(<string>this.selectedEpisodeId);
            } else if (this.operation === "Unmerge") {
                await this.episodeService.unmerge(this._unmergeEpisodeIdChange.value, <string[]>this.selectedEpisodeId);
            }
        } finally {
            this.visible = false;
            this.confirmInProgress = false;
            this.confirmed.next();
        }
    }

    private get onShow$() {
        return this._visibleChange.pipe(
            filter(v => v),
            tap(_ => (this._selectedEpisodeIds = []))
        );
    }

    private get loadMergeEpisodes$() {
        return this.onShow$.pipe(switchMap(_ => of(this.mergeMoveService.mergeEpisodes)));
    }

    private get loadUnmergeEpisodes$() {
        return this.onShow$.pipe(
            combineLatestWith(this._unmergeEpisodeIdChange),
            switchMap(([_, episodeId]) => this.getMergedEpisodes(episodeId))
        );
    }

    private getMergedEpisodes(episodeId: string) {
        return from(this.episodeService.getMergedEpisodes(episodeId)).pipe(
            tap(_ => (this.loadingEpisodes = true)),
            catchError(_ => of(<IEpisode[]>[])),
            tap(_ => (this.loadingEpisodes = false))
        );
    }
}
